import { sharedSchema } from "@shared-database/pgSchema";
import { File } from "@shared-database/models/file";
import { DocumentIntelligence } from "@shared-database/models/document-intelligence";
import {
  leoID,
  leoNotNullForeignID,
  timestampColumns,
} from "@shared-database/utils";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";
import { index, integer, text, uniqueIndex, vector } from "drizzle-orm/pg-core";
import { EMBEDDING_DIMENSIONS } from "@shared/utils/rag/constants/extraction";

/**
 * Ordered text chunks extracted from a canonical file processing result.
 *
 * Chunks are file-scoped and reusable across consumers: profile files use
 * them as the text units for graph extraction/materialization, while
 * chat-uploaded files use the same rows for chunk-only retrieval. The unique
 * `(fileId, chunkIndex)` key keeps reindexing deterministic and prevents
 * workspace/profile scope from leaking into canonical file processing.
 */
export const DocumentChunk = sharedSchema.table(
  "DocumentChunk",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),
    content: text("content").notNull(),
    chunkIndex: integer("chunkIndex").notNull(),
    startOffset: integer("startOffset").notNull(),
    endOffset: integer("endOffset").notNull(),
    tokenCount: integer("tokenCount").notNull(),
    embedding: vector("embedding", { dimensions: EMBEDDING_DIMENSIONS }),

    // References
    fileId: leoNotNullForeignID("fileId").references(() => File.id, {
      onDelete: "cascade",
    }),
    documentIntelligenceId: leoNotNullForeignID(
      "documentIntelligenceId",
    ).references(() => DocumentIntelligence.id, { onDelete: "cascade" }),

    // NOTE: a `contentTsv tsvector GENERATED ALWAYS AS
    // (to_tsvector('simple', content)) STORED` column also exists on this
    // table, indexed by the GIN index `DocumentChunk_contentTsv_gin_idx`.
    // `'simple'` is deliberate so non-English content still tokenises (at
    // the cost of stemming for English). It is intentionally not declared
    // here because drizzle-kit would otherwise regenerate the hand-written
    // migration. Lexical search in `rag/search.ts` references the column
    // via raw SQL.
  },
  (table) => [
    index("DocumentChunk_fileIdx").on(table.fileId),
    index("DocumentChunk_docIntIdx").on(table.documentIntelligenceId),
    uniqueIndex("DocumentChunk_fileChunkIndexUniqueIdx").on(
      table.fileId,
      table.chunkIndex,
    ),
  ],
);

// Types
export type DocumentChunkType = InferSelectModel<typeof DocumentChunk>;
export type DocumentChunkInsertType = InferInsertModel<typeof DocumentChunk>;
